import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "../cn";
import type { Milestone } from "@/domain/types";

// One canonical milestone stepper, replacing the divergent renderers across the app.
// Completed = success fill + check, current = accent ring, future = neutral, and the
// current node gets an amber dot when the project is behind schedule. Renders as a
// connected horizontal track (dashboards) or a vertical list (the public page).
type State = "done" | "current" | "future";

function currentIndex(ms: Milestone[]): number {
  const byFlag = ms.findIndex((m) => m.current);
  if (byFlag >= 0) return byFlag;
  const done = ms.filter((m) => m.status === "disbursed" || m.status === "closed").length;
  return Math.min(done, Math.max(0, ms.length - 1));
}

function stateFor(m: Milestone, i: number, curIdx: number): State {
  if (m.status === "disbursed" || m.status === "closed") return "done";
  return i === curIdx ? "current" : "future";
}

function Node({ state, index, behind }: { state: State; index: number; behind?: boolean }) {
  return (
    <span className="relative">
      <span
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-semibold",
          state === "done"
            ? "border-okFill bg-okFill text-white"
            : state === "current"
            ? "border-brand bg-brandSoft text-brand ring-2 ring-brandRing"
            : "border-line bg-card text-faint",
        )}
      >
        {state === "done" ? <Check className="h-4 w-4" aria-hidden /> : index + 1}
      </span>
      {behind && (
        <span
          className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-card bg-warn"
          aria-label="behind schedule"
        />
      )}
    </span>
  );
}

export function MilestoneStepper({
  milestones,
  orientation = "horizontal",
  driftDays = 0,
  renderAmount,
  statusLabel,
  className,
}: {
  milestones: Milestone[];
  orientation?: "horizontal" | "vertical";
  /** When > 5, the current node shows a behind-schedule amber dot. */
  driftDays?: number;
  /** Optional tranche/amount line under each node (horizontal). */
  renderAmount?: (m: Milestone) => React.ReactNode;
  /** Optional status text per row (vertical); defaults to Complete / In progress / Planned. */
  statusLabel?: (m: Milestone, state: State) => string;
  className?: string;
}) {
  if (milestones.length === 0) return null;
  const curIdx = currentIndex(milestones);
  const behindIdx = driftDays > 5 ? curIdx : -1;

  if (orientation === "vertical") {
    return (
      <ol className={cn("space-y-2", className)}>
        {milestones.map((m, i) => {
          const state = stateFor(m, i, curIdx);
          const label = statusLabel
            ? statusLabel(m, state)
            : state === "done"
            ? "Complete"
            : state === "current"
            ? "In progress"
            : "Planned";
          return (
            <li key={m.id} className="flex items-center gap-3 rounded-lg border border-line bg-card px-4 py-2.5">
              <Node state={state} index={i} behind={i === behindIdx} />
              <span className={cn("flex-1 text-sm", state === "future" ? "text-mute" : "text-ink")}>{m.name}</span>
              <span className="shrink-0 text-xs text-faint">{label}</span>
            </li>
          );
        })}
      </ol>
    );
  }

  return (
    <ol className={cn("flex items-start overflow-x-auto pb-1", className)}>
      {milestones.map((m, i) => {
        const state = stateFor(m, i, curIdx);
        return (
          <li key={m.id} className="flex w-[96px] shrink-0 flex-col items-center">
            <div className="flex w-full items-center">
              <span className={cn("h-0.5 flex-1", i === 0 ? "opacity-0" : i <= curIdx ? "bg-brand" : "bg-line")} aria-hidden />
              <Node state={state} index={i} behind={i === behindIdx} />
              <span className={cn("h-0.5 flex-1", i === milestones.length - 1 ? "opacity-0" : i < curIdx ? "bg-brand" : "bg-line")} aria-hidden />
            </div>
            <p
              className={cn("mt-1.5 line-clamp-2 w-full px-0.5 text-center text-[11px] font-medium", state === "current" ? "text-brand" : "text-ink")}
              title={m.name}
            >
              {m.name}
            </p>
            {renderAmount && <p className="w-full truncate px-1 text-center text-[10px] tabular-nums text-faint">{renderAmount(m)}</p>}
          </li>
        );
      })}
    </ol>
  );
}
